import { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  AuroraCanvas,
  GLASS_NAV_WIDTH,
  GLASS_NAV_COLLAPSED_WIDTH,
  GlassNav,
  GlassNavItem,
  GlassTopBar,
  ThemeToggle,
  applyGlassMode,
  readGlassMode,
} from '@aurora-pvi/design-system/glass';
import { Icon, type IconName } from '@aurora-pvi/design-system';
import { FiltersProvider } from '../app/providers/FiltersProvider';
import { SavedViews } from './SavedViews';
import { useSession } from '../app/providers/SessionProvider';
import { apiFetch } from '../api/apiFetch';
import { applyAccent } from '../features-v2/landing/HeroLandingPage';
import { InlineSearch } from './InlineSearch';
import { GlobalSearch, rememberView } from './GlobalSearch';
import { DemoTour } from './DemoTour';
import { NotificationBell } from './NotificationBell';

/**
 * The Aurora Glass shell.
 *
 * A parallel shell rather than a replacement. Both themes stay live at their own
 * routes so they can be compared side by side — a redesign nobody can put next
 * to the original is a redesign nobody can evaluate.
 */

interface NavItem {
  id: string;
  label: string;
  icon: IconName;
  to: string;
}

interface NavGroup {
  id: string;
  label: string | null;
  items: NavItem[];
}

/**
 * Grouped by the question being asked, not by the subsystem that answers it.
 *
 * **Summary is "what have we got"** — the estate, walked top down in the order
 * somebody would present it: the executive view, the hierarchy under it, then
 * products, the applications beneath those, the processes they serve and the
 * capabilities those processes deliver. It is a sequence rather than a set,
 * which is what `SUMMARY_ROUTES` and the forward arrow depend on.
 *
 * **Strategy is "what are we aiming at, and when does it land"** — the
 * objective cascade, the roadmap it plays out on, and the releases that
 * actually deliver it. These were inside Summary and did not belong there:
 * Objectives sat between Executive and Portfolio, so a walkthrough of the
 * estate detoured through the OKR tree before it had shown anybody the estate.
 * Intent and inventory are different questions and now read as different
 * groups.
 *
 * **People is "who runs it"** — Teams and Workforce, which were the tail of the
 * Summary walkthrough for the same reason of convenience rather than meaning.
 *
 * Analytics is "what does it tell me", Mapping is "what connects to what". The
 * groups below those are unchanged.
 *
 * Teams and Workforce appear once each. They were once named for both Summary
 * and Analytics; two entries pointing at the same route is a menu that makes a
 * reader wonder which one is the real one, and there is only one screen behind
 * each. Split them the moment there is a distinct analytics view.
 *
 * Every entry points at a route that exists — unbuilt screens render an honest
 * placeholder rather than a 404.
 */
const NAV_GROUPS: NavGroup[] = [
  {
    id: 'summary',
    label: 'Summary',
    items: [
      { id: 'executive', label: 'Executive', icon: 'gauge', to: '/v2/executive' },
      { id: 'portfolio', label: 'Portfolio', icon: 'hierarchy', to: '/v2/portfolio' },
      { id: 'products', label: 'Products', icon: 'cube', to: '/v2/products' },
      { id: 'applications', label: 'Applications', icon: 'apps', to: '/v2/applications' },
      // Process before Capabilities: a process is the thing the business does
      // and a capability is what that process delivers, so the catalogue reads
      // before the abstraction over it.
      { id: 'process-catalog', label: 'Process catalog', icon: 'flow', to: '/v2/process' },
      { id: 'capabilities', label: 'Capabilities', icon: 'grid', to: '/v2/capabilities' },
      // Part of the estate, not a showcase beside it: an agent that writes the
      // roster belongs in the same walkthrough as the application that stores
      // it. Last in Summary because it is the newest layer over the rest.
      { id: 'agents', label: 'AI agents', icon: 'compass', to: '/v2/agents' },
    ],
  },
  {
    id: 'strategy',
    label: 'Strategy',
    items: [
      { id: 'objectives', label: 'Objectives', icon: 'target', to: '/v2/objectives' },
      { id: 'roadmap', label: 'Roadmap', icon: 'columns', to: '/v2/roadmap' },
      // Beside the roadmap rather than beside Delivery. Both answer "when", and
      // a reader looking for what lands next month looks where the plan is; the
      // Delivery group answers "how fast and how safely", which is a different
      // question over a different unit.
      { id: 'releases', label: 'Release calendar', icon: 'columns', to: '/v2/releases' },
    ],
  },
  {
    id: 'people',
    label: 'People',
    items: [
      { id: 'teams', label: 'Teams', icon: 'people', to: '/v2/teams' },
      { id: 'workforce', label: 'Workforce', icon: 'people', to: '/v2/workforce' },
    ],
  },
  {
    id: 'analytics',
    label: 'Analytics',
    items: [
      { id: 'investment', label: 'Investment', icon: 'coins', to: '/v2/investment' },
      // Funding and Investment are two halves of one question — what we agreed
      // to spend and what we spent — so they sit next to each other.
      { id: 'funding', label: 'Funding & forecast', icon: 'coins', to: '/v2/funding' },
      { id: 'capacity', label: 'Capacity & demand', icon: 'people', to: '/v2/capacity' },
      // "Delivery & flow" is now only delivery — DORA. Flow has its own entry
      // because it is a different question over a different unit of analysis,
      // and one label covering both was the reason nobody could find either.
      { id: 'delivery', label: 'Delivery & DORA', icon: 'activity', to: '/v2/delivery' },
      { id: 'flow', label: 'Value stream flow', icon: 'activity', to: '/v2/delivery/flow' },
      { id: 'outcomes', label: 'Outcome tracing', icon: 'target', to: '/v2/delivery/outcomes' },
      { id: 'compare', label: 'Compare periods', icon: 'columns', to: '/v2/portfolio/compare' },
      { id: 'risks', label: 'Risk register', icon: 'target', to: '/v2/risks' },
      { id: 'technology', label: 'Technology estate', icon: 'columns', to: '/v2/technology' },
      { id: 'duplicates', label: 'Duplicates', icon: 'columns', to: '/v2/applications/duplicates' },
      {
        id: 'heatmap',
        label: 'Technology heatmap',
        icon: 'target',
        to: '/v2/applications/heatmap',
      },
    ],
  },
  {
    id: 'mapping',
    label: 'Mapping',
    items: [
      {
        id: 'process-mapping',
        label: 'Process mapping',
        icon: 'flow',
        to: '/v2/mapping/process-product',
      },
      {
        id: 'app-mapping',
        label: 'Application mapping',
        icon: 'grid',
        to: '/v2/mapping/application-product',
      },
    ],
  },
  {
    id: 'discover',
    label: 'Discovery',
    items: [
      { id: 'candidates', label: 'Candidates', icon: 'compass', to: '/v2/discover/candidates' },
      { id: 'scenarios', label: 'Scenarios', icon: 'sliders', to: '/v2/scenarios' },
    ],
  },
  {
    id: 'commercial',
    label: 'Commercial',
    items: [
      { id: 'suppliers', label: 'Suppliers', icon: 'truck', to: '/v2/suppliers' },
      { id: 'contracts', label: 'Contracts', icon: 'contract', to: '/v2/contracts' },
    ],
  },
  {
    id: 'data',
    label: 'Data',
    items: [
      { id: 'import', label: 'Import center', icon: 'upload', to: '/v2/data/import' },
      { id: 'integrations', label: 'Integrations', icon: 'plug', to: '/v2/admin/integrations' },
      { id: 'lineage', label: 'Lineage & quality', icon: 'lineage', to: '/v2/admin/lineage' },
    ],
  },
  {
    id: 'governance',
    label: 'Governance',
    items: [
      {
        id: 'command-center',
        label: 'Command center',
        icon: 'shield',
        to: '/v2/admin/command-center',
      },
      { id: 'approvals', label: 'Approvals', icon: 'check', to: '/v2/admin/approvals' },
      { id: 'collaboration', label: 'Decisions & actions', icon: 'edit', to: '/v2/collaboration' },
      { id: 'taxonomy', label: 'Taxonomy', icon: 'database', to: '/v2/admin/taxonomy' },
      { id: 'fields', label: 'Your own fields', icon: 'grid', to: '/v2/admin/fields' },
      { id: 'roles', label: 'Roles & access', icon: 'lock', to: '/v2/admin/roles' },
      { id: 'delegation', label: 'Delegation', icon: 'user', to: '/v2/admin/delegation' },
    ],
  },
  {
    id: 'personal',
    label: 'Yours',
    items: [
      { id: 'watchlist', label: 'Watchlist', icon: 'star', to: '/v2/watchlist' },
      { id: 'reports', label: 'Reports', icon: 'file', to: '/v2/reports' },
      { id: 'calculations', label: 'How figures are calculated', icon: 'info', to: '/v2/calculations' },
    ],
  },
];

const NAV_ITEMS: NavItem[] = NAV_GROUPS.flatMap((group) => group.items);

/**
 * The Summary walkthrough, in order.
 *
 * Exported so the forward arrow on those screens follows the rail rather than
 * a second copy of the same list — a reordered menu must not leave the arrows
 * stepping through a path that no longer exists.
 */
export const SUMMARY_ROUTES: ReadonlyArray<{ to: string; label: string }> = (
  NAV_GROUPS.find((group) => group.id === 'summary')?.items ?? []
).map((item) => ({ to: item.to, label: item.label }));

/** Bar height (64) + top inset (12) + the gap below it (12). */
const GLASS_TOPBAR_CLEARANCE = 88;

/** Matches GlassNav's own default inset, so the rail and the column agree. */
const GLASS_NAV_INSET = 12;
/** The gap between cards, reused as the gap between the rail and the page. */
const GLASS_GUTTER = 12;

/** Guarded so the shell can still render where `matchMedia` is absent (tests, SSR). */
function matches(query: string): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(query).matches;
}

export function GlassShell() {
  const { currentUser, canOpen } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  /**
   * Seeded from the media query rather than defaulted to `false` and corrected
   * in an effect. The effect version renders one frame of the desktop layout —
   * a 248px rail and a content column indented to clear it — and then animates
   * back, so every phone load begins with a visible sideways lurch.
   */
  /**
   * The guided tour lives in the shell, not on the screen that starts it.
   * It navigates between routes, so anything owning it below the router would
   * unmount itself on the first step.
   */
  const [tourRunning, setTourRunning] = useState(false);
  useEffect(() => {
    const start = () => setTourRunning(true);
    window.addEventListener('aurora:start-tour', start);
    return () => window.removeEventListener('aurora:start-tour', start);
  }, []);

  const [collapsed, setCollapsed] = useState(() => matches('(max-width: 1279px)'));
  const [compact, setCompact] = useState(() => matches('(max-width: 767px)'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  /**
   * The reader's shell colour, restored on arrival.
   *
   * The landing page applies it as they choose it, but somebody who bookmarks
   * a screen never passes through the landing page — and a preference that
   * only works on one route is not a preference.
   */
  useEffect(() => {
    let cancelled = false;
    void apiFetch(`${import.meta.env.VITE_API_BASE_URL}/me/preferences`)
      .then((response) => (response.ok ? response.json() : null))
      .then((body: { data?: { shell_accent?: string } } | null) => {
        const saved = body?.data?.shell_accent;
        if (!cancelled && saved) applyAccent(saved);
      })
      .catch(() => {
        // The default palette is a perfectly good fallback.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The theme attribute goes on <html>, not on a wrapper: the aurora canvas is
  // fixed to the viewport and the custom properties have to reach it.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme-v2', 'glass');
    applyGlassMode(readGlassMode());
    // Restore the classic theme on exit, or the old screens render against dark
    // tokens and look broken.
    return () => {
      root.removeAttribute('data-theme-v2');
      root.removeAttribute('data-mode');
    };
  }, []);

  useEffect(() => {
    const query = window.matchMedia('(max-width: 1279px)');
    const apply = (matches: boolean) => setCollapsed(matches);
    apply(query.matches);
    const listener = (event: MediaQueryListEvent) => apply(event.matches);
    query.addEventListener('change', listener);
    return () => query.removeEventListener('change', listener);
  }, []);

  /**
   * Below this width the rail becomes a drawer. A persistent rail costs 88 of
   * 375 points — a quarter of the screen — and collapsing it to icons only
   * trades that for fifteen unlabelled glyphs. Neither is navigation.
   */
  useEffect(() => {
    const query = window.matchMedia('(max-width: 767px)');
    const apply = (matches: boolean) => setCompact(matches);
    apply(query.matches);
    const listener = (event: MediaQueryListEvent) => apply(event.matches);
    query.addEventListener('change', listener);
    return () => query.removeEventListener('change', listener);
  }, []);

  // A drawer that survives navigation would cover the screen the user just
  // asked for.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // ⌘K / Ctrl-K opens search from anywhere. Bound on the shell rather than in
  // the palette so the shortcut works when the palette is closed, which is the
  // only time it matters.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Longest match wins, so /v2/mapping/process-product selects the mapping item
  // rather than whichever shorter prefix happened to be declared first.
  const activeId =
    [...NAV_ITEMS]
      .sort((a, b) => b.to.length - a.to.length)
      .find((item) => location.pathname.startsWith(item.to))?.id ?? 'portfolio';

  // In drawer mode the rail is out of the layout, so the content column claims
  // the whole width rather than padding around something that is not there.
  const railWidth = compact ? 0 : collapsed ? GLASS_NAV_COLLAPSED_WIDTH : GLASS_NAV_WIDTH;

  /**
   * Where the content column starts.
   *
   * The rail is `position: fixed` at `left: GLASS_NAV_INSET` and is
   * `railWidth` wide, so its right edge is at inset + railWidth. Padding the
   * content by `railWidth + 12` therefore put the first card *exactly* on that
   * edge — the two surfaces touched, with no gutter between the navigation and
   * the page. The column now clears the rail's real right edge and then adds a
   * gutter the same size as the gap between cards.
   */
  const contentLeft = compact ? GLASS_GUTTER : GLASS_NAV_INSET + railWidth + GLASS_GUTTER;

  const activeItem = NAV_ITEMS.find((item) => item.id === activeId) ?? null;

  // Recorded on arrival rather than on click, so a deep link or a back button
  // counts as a visit — which is the whole point of a recent-items list.
  useEffect(() => {
    if (activeItem) rememberView(activeItem.to, activeItem.label);
  }, [activeItem]);

  const destinations = useMemo(
    () =>
      NAV_GROUPS.flatMap((group) =>
        group.items.map((item) => ({
          id: item.id,
          label: item.label,
          to: item.to,
          group: group.label,
        })),
      ),
    [],
  );

  return (
    <FiltersProvider>
      <div className="relative min-h-screen text-[var(--g-text)]">
        <AuroraCanvas />

        <GlassNav
          activeId={activeId}
          collapsed={collapsed}
          onToggle={compact ? undefined : () => setCollapsed((value) => !value)}
          overlay={compact}
          open={drawerOpen}
          onDismiss={() => setDrawerOpen(false)}
          label="Main navigation"
          header={<AuroraMark collapsed={!compact && collapsed} />}
        >
          {/*
            A page this person cannot open is absent, and so is a group left
            empty by that — a heading with nothing under it advertises what
            somebody is not allowed to see. Presentation only: the route guard
            below and the API both refuse independently.
          */}
          {NAV_GROUPS.map((group) => ({
            ...group,
            items: group.items.filter((item) => canOpen(item.to)),
          }))
            .filter((group) => group.items.length > 0)
            .map((group) => (
            <div key={group.id} className="mb-3 last:mb-0">
              {group.label && (compact || !collapsed) && (
                <h2 className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.09em] text-[var(--g-text-faint)]">
                  {group.label}
                </h2>
              )}
              {group.items.map((item) => (
                <GlassNavItem
                  key={item.id}
                  id={item.id}
                  label={item.label}
                  href={item.to}
                  icon={<Icon name={item.icon} className="h-4 w-4" aria-hidden />}
                  onSelect={() => navigate(item.to)}
                />
              ))}
            </div>
          ))}
        </GlassNav>

        {/* The top bar is position:fixed, so the content column has to clear it
            explicitly — GLASS_TOPBAR_CLEARANCE is the bar height plus both
            insets. Letting content slide under a translucent bar is the classic
            glass-UI failure: the blur makes it look intentional right up until
            someone tries to read the heading. */}
        <div
          className="relative z-10 flex min-h-screen flex-col gap-3 p-3 transition-[padding] duration-200"
          style={{ paddingLeft: contentLeft, paddingTop: GLASS_TOPBAR_CLEARANCE }}
        >
          <GlassTopBar
            eyebrow={undefined}
            title={undefined}
            offsetLeft={contentLeft}
            center={
              !compact ? (
                <div className="flex w-full items-center gap-3">
                  {/* The search *is* the bar now — it takes the width the
                      masthead and breadcrumb used to occupy. */}
                  <InlineSearch destinations={destinations} />
                </div>
              ) : compact ? (
                <div className="flex w-full items-center gap-2.5">
                  <button
                    type="button"
                    onClick={() => setDrawerOpen(true)}
                    aria-label="Open navigation"
                    aria-expanded={drawerOpen}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border border-[var(--g-edge)] text-[var(--g-text-secondary)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--g-accent)]"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      aria-hidden
                    >
                      <path d="M4 7h16M4 12h16M4 17h16" />
                    </svg>
                  </button>
                  {/* The current screen, not the product name: on a phone the
                      bar has room for one label and "where am I" beats
                      "what is this". */}
                  <span className="truncate text-[14px] font-medium text-[var(--g-text)]">
                    {activeItem?.label ?? 'Aurora'}
                  </span>
                  <button
                    type="button"
                    onClick={() => setSearchOpen(true)}
                    aria-label="Search"
                    className="ml-auto flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border border-[var(--g-edge)] text-[var(--g-text-secondary)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--g-accent)]"
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
                      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                      <path
                        d="m20 20-3.5-3.5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                </div>
              ) : undefined
            }
            actions={
              <div className="flex items-center gap-2">
                <SavedViews />
                <NotificationBell />
                <ThemeToggle />
                {/* The identity is a control, not a caption. Span of control is
                    enforced server-side against whoever this is, so being able
                    to change it is how the narrowing is seen at all. */}
                <button
                  type="button"
                  onClick={() => navigate('/v2/signin')}
                  aria-label={`Signed in as ${currentUser.displayName}, ${currentUser.roleLabel}. Change identity.`}
                  className="hidden flex-col items-end rounded-[10px] px-2 py-1 leading-tight transition-colors hover:bg-[var(--g-surface-2)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--g-accent)] sm:flex"
                >
                  <span className="text-[13px] font-medium text-[var(--g-text)]">
                    {currentUser.displayName}
                  </span>
                  <span className="text-[11px] text-[var(--g-text-muted)]">
                    {currentUser.roleLabel}
                  </span>
                </button>
              </div>
            }
          />

          <main id="main-content" className="min-h-0 flex-1" tabIndex={-1}>
            <Outlet />
          </main>
        </div>

        <GlobalSearch
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          destinations={destinations}
        />

        {tourRunning ? <DemoTour onExit={() => setTourRunning(false)} /> : null}
      </div>
    </FiltersProvider>
  );
}

function AuroraMark({ collapsed }: { collapsed: boolean }) {
  return (
    <div className="flex items-center gap-2.5">
      <svg viewBox="0 0 32 32" className="h-7 w-7 shrink-0" role="img" aria-label="Aurora Airlines">
        <defs>
          <linearGradient id="g-mark" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="var(--g-aurora-1)" />
            <stop offset="100%" stopColor="var(--g-aurora-2)" />
          </linearGradient>
        </defs>
        <path
          d="M6 21 L16 7 L26 21 M11 21 L16 13.5 L21 21"
          fill="none"
          stroke="url(#g-mark)"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {/* Mark only. The wordmark used to read "AURORA GLASS"; the product is
          not called that to anyone outside this repository. */}
      {!collapsed && (
        <span className="text-[13px] font-semibold tracking-tight text-[var(--g-text)]">
          Aurora
        </span>
      )}
    </div>
  );
}
